import { useSelector } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { DesignSystemToken, StringTokenValue, TokenValue } from 'designSystem/types';
import { resolveTokenValue } from 'designSystem/tokens/resolver';
import { BorderRadiusTokenPreview } from './BorderRadiusTokenPreview';
import { ColorTokenPreview } from './ColorTokenPreview';
import { FontFamilyTokenPreview } from './FontFamilyTokenPreview';
import { FontSizeTokenPreview } from './FontSizeTokenPreview';
import { FontWeightTokenPreview } from './FontWeightTokenPreview';
import { LengthTokenPreview } from './LengthTokenPreview';
import { LineHeightTokenPreview } from './LineHeightTokenPreview';
import { OpacityTokenPreview } from './OpacityTokenPreview';
import { OtherTokenPreview } from './OtherTokenPreview';
import { ShadowTokenPreview } from './ShadowTokenPreview';
import { SpacingTokenPreview } from './SpacingTokenPreview';
import { StylesColorTokenPreview } from './StylesColorTokenPreview';
import { TransitionTokenPreview } from './TransitionTokenPreview';
import * as S from './styles';

interface TokenCardProps {
	token: DesignSystemToken;
	onClick?: () => void;
}

// Check if a string value is a valid CSS color
// Detects hex, rgb/rgba, hsl/hsla, and key CSS color keywords
function isColorValue(value: string): boolean {
	const trimmed = value.trim().toLowerCase();

	// Hex colors: #rgb, #rgba, #rrggbb, #rrggbbaa
	if (/^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/.test(trimmed)) {
		return true;
	}

	// RGB/RGBA or HSL/HSLA functional notation
	if (/^(rgba?|hsla?)\s*\(/.test(trimmed)) {
		return true;
	}

	// Essential CSS color keywords (design tokens rarely use named colors, but these are common)
	const colorKeywords = new Set(['transparent', 'currentcolor', 'white', 'black']);
	return colorKeywords.has(trimmed);
}

// Check if a token value resolves to a color
// Resolves var() references and checks the actual underlying value
function resolvesToColor(originalValue: string): boolean {
	// First resolve any var() references to their actual values
	const resolved = resolveTokenValue(originalValue);

	// Check if the resolved value is a color
	return isColorValue(resolved);
}

// Strips var() wrapper, e.g. "var(--theme-blue-800)" -> "--theme-blue-800"
function stripVar(value: string): string {
	return value.replace(/^var\(([^)]+)\)$/, '$1');
}

// Formats a token value for display in the tooltip
function formatTokenValue(value: TokenValue): string {
	switch (value.kind) {
		case 'color':
			return value.hex;
		case 'length':
			return value.px !== undefined ? `${value.px}px` : value.original;
		case 'fontFamily':
			return value.families.join(', ');
		case 'fontSize':
			return value.length.original;
		case 'fontWeight':
			return value.keyword || String(value.numeric);
		case 'lineHeight':
			return value.original;
		case 'borderRadius':
			return value.length.original;
		case 'opacity':
			return `${Math.round(value.value * 100)}%`;
		case 'transition':
			return `${value.duration} ${value.timingFunction}`;
		case 'string':
			return stripVar(value.original);
		default:
			return stripVar(value.original);
	}
}

export const TokenCard = ({ token, onClick }: TokenCardProps) => {
	const selected = useSelector(
		(r: RootReducer) =>
			r.mode.selectedDsPicks.some((p) => p.kind === 'token' && p.cssVariable === token.cssVariable)
	);
	const renderPreview = () => {
		// Use SpacingTokenPreview for spacing category tokens
		if (token.category === 'spacing' && token.value.kind === 'length') {
			return <SpacingTokenPreview value={token.value} />;
		}

		// Handle string tokens that resolve to colors (via var() references or direct values)
		// This robustly detects colors by resolving the actual value, not by naming conventions
		const isColor = token.category === 'color' || resolvesToColor(token.value.original);
		if (token.value.kind === 'string' && isColor) {
			return <StylesColorTokenPreview value={token.value as StringTokenValue} />;
		}

		switch (token.value.kind) {
			case 'color':
				return <ColorTokenPreview value={token.value} />;
			case 'length':
				return <LengthTokenPreview value={token.value} />;
			case 'shadow':
				return <ShadowTokenPreview value={token.value} />;
			case 'fontFamily':
				return <FontFamilyTokenPreview value={token.value} />;
			case 'fontSize':
				return <FontSizeTokenPreview value={token.value} />;
			case 'lineHeight':
				return <LineHeightTokenPreview value={token.value} />;
			case 'fontWeight':
				return <FontWeightTokenPreview value={token.value} />;
			case 'borderRadius':
				return <BorderRadiusTokenPreview value={token.value} />;
			case 'opacity':
				return <OpacityTokenPreview value={token.value} />;
			case 'transition':
				return <TransitionTokenPreview value={token.value} />;
			case 'number':
			case 'string':
				return <OtherTokenPreview value={token.value} />;
			default:
				return <OtherTokenPreview value={token.value as any} />;
		}
	};

	return (
		<S.TokenCard $selected={selected} onClick={onClick}>
			<S.TokenTooltip>{formatTokenValue(token.value)}</S.TokenTooltip>
			{renderPreview()}
			<S.TokenName>{token.name}</S.TokenName>
			<S.TokenCssVariable>{token.cssVariable}</S.TokenCssVariable>
		</S.TokenCard>
	);
};
